/**
 * Deep, unfrozen copy of a value Apollo handed back.
 *
 * Apollo deep-freezes cached results — `maybeDeepFreeze` guards on
 * `globalThis.__DEV__ !== false` and nothing in this repo defines `__DEV__`, so the freeze
 * is on in production builds too. Anything that then flows into a Vue reactive graph is a
 * hazard, and not only for the obvious reason:
 *
 * - Writing to a frozen object throws `Cannot add property …, object is not extensible`.
 * - Less obvious: a **frozen array of objects** reaching vee-validate's form values makes
 *   `isDirty`'s deep `isEqual` walk it through a reactive proxy, which throws
 *   `'get' on proxy: property '0' is a read-only and non-configurable data property …`.
 *   The array's own index is non-writable while the element it holds is reactive-wrapped,
 *   so the proxy cannot report the raw value and the JS invariant fails.
 *
 * A shallow copy does **not** close this: copying the array alone still leaves frozen
 * elements, and copying the record alone still leaves frozen arrays. The whole subtree has
 * to be fresh, which is what this does.
 *
 * `structuredClone` is safe here because GraphQL results are plain JSON — objects, arrays,
 * strings, numbers, booleans and nulls. It would throw on a function or a symbol; nothing
 * that comes off the wire carries one.
 *
 * Prefer a real domain mapper where one exists — `fromGql` implementations rebuild their
 * output and are already thawed by construction. This is for the deliberate passthroughs,
 * where the generated GraphQL shape *is* the shape consumers read.
 */
export const thawApolloResult = <T>(value: T): T => structuredClone(value);
